import { apiClient } from './api'
import type {
  TestSetWithStats,
  TestSetWithDetails,
  EvaluationRunSummary,
  EvaluationRunDetail,
  EvaluationResult,
  EvaluationAnalytics,
  TestCaseWithEvaluations,
  JudgeResult
} from '@shared/types/evaluation'

// Request types based on backend schemas
export interface CreateTestSetRequest {
  name: string
  description?: string
  systemPrompt: string
  model?: string
  temperature?: number
}

export interface UpdateTestSetRequest {
  name?: string
  description?: string
  systemPrompt?: string
  model?: string
  temperature?: number
}

export interface TestSetQueryParams {
  search?: string
  sortBy?: 'createdAt' | 'name' | 'updatedAt'
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface CreateTestCaseRequest {
  question: string
  goldenAnswer: string
  category?: string
  tags?: string[]
  difficulty?: 'easy' | 'medium' | 'hard'
}

export interface UpdateTestCaseRequest {
  question?: string
  goldenAnswer?: string
  category?: string
  tags?: string[]
  difficulty?: 'easy' | 'medium' | 'hard'
}

export interface BulkTestCasesRequest {
  testCases: CreateTestCaseRequest[]
}

export interface RunEvaluationRequest {
  passingScore?: number
}

export interface TestJudgeRequest {
  question: string
  goldenAnswer: string
  ragResponse: string
  passingScore?: number
}

export interface ReorderTestCasesRequest {
  testCaseIds: string[]
}

export const evaluationService = {
  // Test Set Management
  async getTestSets(params?: TestSetQueryParams): Promise<TestSetWithStats[]> {
    const response = await apiClient.get('/evaluation', { params })
    const data = (response.data as any)?.data
    return data?.testSets || data || []
  },

  async getTestSet(testSetId: string): Promise<TestSetWithDetails> {
    const response = await apiClient.get(`/evaluation/${testSetId}`)
    return (response.data as any)?.data || response.data
  },

  async createTestSet(data: CreateTestSetRequest): Promise<TestSetWithStats> {
    const response = await apiClient.post('/evaluation', data)
    return (response.data as any)?.data || response.data
  },

  async updateTestSet(testSetId: string, data: UpdateTestSetRequest): Promise<TestSetWithStats> {
    const response = await apiClient.put(`/evaluation/${testSetId}`, data)
    return (response.data as any)?.data || response.data
  },

  async deleteTestSet(testSetId: string): Promise<void> {
    await apiClient.delete(`/evaluation/${testSetId}`)
  },

  // Test Case Management
  async addTestCase(testSetId: string, testCase: CreateTestCaseRequest): Promise<TestCaseWithEvaluations> {
    const response = await apiClient.post(`/evaluation/${testSetId}/cases`, testCase)
    return (response.data as any)?.data || response.data
  },

  async bulkAddTestCases(testSetId: string, data: BulkTestCasesRequest): Promise<TestCaseWithEvaluations[]> {
    const response = await apiClient.post(`/evaluation/${testSetId}/cases/bulk`, data)
    return (response.data as any)?.data || response.data
  },

  async updateTestCase(testSetId: string, testCaseId: string, data: UpdateTestCaseRequest): Promise<TestCaseWithEvaluations> {
    const response = await apiClient.put(`/evaluation/${testSetId}/cases/${testCaseId}`, data)
    return (response.data as any)?.data || response.data
  },

  async reorderTestCases(testSetId: string, data: ReorderTestCasesRequest): Promise<void> {
    await apiClient.put(`/evaluation/${testSetId}/cases/reorder`, data)
  },

  // Evaluation Execution
  async runEvaluation(testSetId: string, options: RunEvaluationRequest = {}): Promise<EvaluationRunSummary> {
    const response = await apiClient.post(`/evaluation/${testSetId}/run`, options)
    return (response.data as any)?.data || response.data
  },

  async getEvaluationRun(runId: string): Promise<EvaluationRunDetail> {
    const response = await apiClient.get(`/evaluation/runs/${runId}`)
    return (response.data as any)?.data || response.data
  },

  async getLatestResults(testSetId: string): Promise<EvaluationResult[]> {
    const response = await apiClient.get(`/evaluation/${testSetId}/results/latest`)
    return (response.data as any)?.data || response.data
  },

  async getEvaluationHistory(testSetId: string, limit: number = 10): Promise<EvaluationRunSummary[]> {
    const response = await apiClient.get(`/evaluation/${testSetId}/history`, {
      params: { limit }
    })
    return (response.data as any)?.data || response.data
  },

  // Analytics
  async getEvaluationStats(): Promise<EvaluationAnalytics> {
    const response = await apiClient.get('/evaluation/analytics/stats')
    return (response.data as any)?.data || response.data
  },

  // Development/Testing
  async testJudge(data: TestJudgeRequest): Promise<JudgeResult> {
    const response = await apiClient.post('/evaluation/test/judge', data)
    return (response.data as any)?.data || response.data
  }
}